"use client";
import { default as ProductComponent } from "../../../components/product";
import { observer } from "mobx-react-lite";
import { ChevronDown, ChevronUp } from "lucide-react";
import cart from "observables/cart";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import useAuthenticatedApi from "hooks/use-authenticated-api";
import { Combo as ComboType } from "interfaces/combos";
import Combo from "components/combo";
import useStoreId from "hooks/use-store-id";
import CustomerHeader from "components/customer-header";

const Home = () => {
  const { push } = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const extras = useRef<Array<number>>([]);
  const hasExtras = useRef<boolean>(false);

  const { storeId } = useStoreId();

  const [products, setProducts] = useState<Record<string, Array<any>>>();
  const [collapsedSections, setCollapsedSection] = useState<Array<string>>([]);
  const [isComboCollapsed, setIsComboCollapsed] = useState<boolean>(false);
  // Add state to track quantity changes
  const [prevQuantity, setPrevQuantity] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const initProducts = async () => {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/stores/${storeId}/catalogue`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
        }
      );

      const products = await response.json();

      const groupedProducts = groupBy(products, ({ type }) => type.name);

      if (!cart.customer?.email) cart.loadCartFromLocalStorage();

      setProducts(
        groupedProducts as {
          Pizza: Array<any>;
          Burger: Array<any>;
          Soft: Array<any>;
        }
      );
    };

    cart.setPromotion(0);

    initProducts();
  }, []);

  useEffect(() => {
    const initHasExtras = async () => {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/stores/${storeId}/catalogue/has-extras`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
        }
      );

      hasExtras.current = (await response.json()).hasExtras;
    };

    initHasExtras();
  }, []);

  const handleSelectVariant = async (variantId: number) => {
    if (!hasExtras.current) return;

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_ORIGIN}/stores/${storeId}/catalogue/variants/${variantId}/extras`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json", // Adjust the content type as needed
          // Add any other headers if required
        },
      }
    );

    const _extras = await response.json();

    if (!_extras || _extras.length === 0) return;

    extras.current.push(variantId);
  };

  const totalQuantity =
    (cart.items?.reduce((acc, current) => (acc += current.quantity), 0) || 0) +
    (cart.extras?.reduce((acc, current) => (acc += current.quantity), 0) || 0);

  // Add effect to detect quantity changes and trigger animation
  useEffect(() => {
    // Check for both increases and decreases in quantity
    if (totalQuantity !== prevQuantity) {
      // Only animate if there are items in the cart
      if (totalQuantity > 0) {
        setIsAnimating(true);
        const timer = setTimeout(() => setIsAnimating(false), 500);
        return () => clearTimeout(timer);
      }
    }
    setPrevQuantity(totalQuantity);
  }, [totalQuantity, prevQuantity]);

  const handleClickProceedToComplete = async () => {
    let order;
    if (
      cart.publicToken &&
      cart.publicToken !== "null" &&
      cart.orderId &&
      cart.orderId !== "null"
    ) {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/stores/${storeId}/customer-orders/${cart.orderId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            picksUpAt: cart.picksUpAt,
            orderElements: cart.items,
            orderCombos: Object.values(cart.combos),
            publicToken: cart.publicToken,
          }),
        }
      );

      order = await response.json();
      // update order
    } else {
      // create order
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/stores/${storeId}/customer-orders`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            orderElements: cart.items,
            orderCombos: Object.values(cart.combos),
          }),
        }
      );

      const result = await response.json();

      if (result.statusCode && !String(result.statusCode).startsWith("2")) {
        throw result;
      }

      order = result;
      cart.setPublicToken(result.publicToken);
      cart.setOrderId(result.orderId);
    }

    cart.setBackendAmount(
      Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(order.amountAfterVat)
    );
    cart.setPromotion(0);
    cart.setElements(order.elements);
    cart.setExtras(order.extraElements);

    push(
      `/o/${params.slug}/${
        hasExtras.current && extras.current.length > 0 ? "extras" : "time"
      }${
        searchParams.get("promo") ? `?promo=${searchParams.get("promo")}` : ""
      }`
    );
  };

  const { data: combos } = useAuthenticatedApi<Array<ComboType>>(
    `/stores/${storeId}/combos/available`,
    "",
    "GET"
  );

  const handleToggleCollapse = (id: string) => () => {
    setCollapsedSection((prevCollapsedSection) =>
      prevCollapsedSection.includes(id)
        ? prevCollapsedSection.filter((e) => e !== id)
        : [...prevCollapsedSection, id]
    );
  };

  if (!products) return null;

  return (
    <div className="flex flex-col min-h-screen">
      {cart.items?.length > 0 && (
        <CustomerHeader
          onClickNext={handleClickProceedToComplete}
          onClickEmptyCart={() => {
            extras.current = [];
          }}
          onClickRemove={(variantId) => {
            // Remove the item from the cart
            const itemToRemove = cart.items.find(
              (item) => item.variant.id === variantId
            );
            if (itemToRemove) {
              // If this item has extras, remove them from extras.current
              if (itemToRemove.id) {
                extras.current = extras.current.filter(
                  (id) => id !== variantId
                );
              }

              // Remove the item from the cart
              cart.removeElement(itemToRemove.id);
            }
          }}
          onClickRemoveCombo={(variantIds) => {
            extras.current = extras.current.reduce(
              (acc, current) => {
                const [variantId, ...rest] = acc.rest;
                if (current === variantId) {
                  return {
                    collection: acc.collection,
                    rest: rest,
                  };
                }

                return {
                  collection: [...acc.collection, current],
                  rest: rest,
                };
              },
              {
                collection: [],
                rest: variantIds,
              }
            ).collection;
          }}
          isAnimating={isAnimating}
        />
      )}

      <main className="space-y-4 p-2 mt-16">
        {combos && combos?.length > 0 && (
          <section className="space-y-4">
            <div
              className="flex justify-between items-center"
              role="button"
              onClick={() =>
                setIsComboCollapsed(
                  (prevIsComboCollapsed) => !prevIsComboCollapsed
                )
              }
            >
              <h1 className="text-5xl">{"Formules"}</h1>

              {!isComboCollapsed ? (
                <ChevronUp className="w-6 h-6" />
              ) : (
                <ChevronDown className="w-6 h-6" />
              )}
            </div>

            {!isComboCollapsed && (
              <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {combos.map((combo) => (
                  <Combo
                    key={combo.id}
                    combo={combo}
                    onClickAddCombo={(variantIds) => {
                      extras.current = [...extras.current, ...variantIds];
                    }}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {sortBy(Object.entries(products), (e) => e[1][0].type.position).map(
          ([key, values]) => {
            return (
              <section key={key} className="space-y-4">
                <div
                  className="flex justify-between items-center"
                  role="button"
                  onClick={handleToggleCollapse(key)}
                >
                  <h1 className="text-5xl">{key}</h1>

                  {!collapsedSections.includes(key) ? (
                    <ChevronUp className="w-6 h-6" />
                  ) : (
                    <ChevronDown className="w-6 h-6" />
                  )}
                </div>
                {!collapsedSections.includes(key) && (
                  <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {values.map((product) => (
                      <ProductComponent
                        key={product.name}
                        product={product}
                        shouldDisplayIngredients
                        onSelectVariant={handleSelectVariant}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          }
        )}
      </main>
    </div>
  );
};

export default observer(Home);
